// Subscription reminder banner (Phase 4) — tenant layout.
//
// Mounts once in the tenant shell (Layout), so it fetches my-subscription a
// SINGLE time per app load (not per route). Shows an always-visible bar when the
// tenant is NOT suspended and is within 3 days of renewal / trial end (or already
// overdue). Admins get a Pay-now CTA (→ billing page); non-admins get an info
// variant. Dismissible for the session (reappears on next load while still ≤3).
//
// Suspended tenants are intentionally NOT shown here — suspension is handled by
// the access gate (server-side 402) + the billing page, not this reminder.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getMySubscription, type MySubscription } from '../api/myBilling';

const THRESHOLD_DAYS = 3;

export function SubscriptionBanner() {
  const { t } = useTranslation();
  const { agent, isAuthenticated } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const [sub, setSub] = useState<MySubscription | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Billing is admin-only: GET /api/billing/my-subscription is admin-guarded, and
    // only admins can pay. Non-admins never fetch it (avoids a 403 on every load).
    if (!isAuthenticated || !isAdmin) return;
    let alive = true;
    // Fetch once for the whole session (this component is mounted once in Layout).
    getMySubscription()
      .then((s) => { if (alive) setSub(s); })
      .catch(() => { /* never break the shell on a billing fetch error */ });
    return () => { alive = false; };
  }, [isAuthenticated, isAdmin]);

  if (dismissed || !sub || !sub.enrolled || sub.suspended) return null;
  if (sub.daysLeft === null || sub.daysLeft > THRESHOLD_DAYS) return null;

  const overdue = sub.status === 'past_due' || sub.daysLeft <= 0;
  const message = overdue
    ? t('billing.banner_overdue')
    : t('billing.banner_due_soon', { count: sub.daysLeft });

  return (
    <div
      role="alert"
      data-testid="subscription-banner"
      className="flex items-center gap-3 px-4 py-2.5 text-sm border-b border-default"
      style={{ backgroundColor: 'var(--tint-danger, rgba(220,38,38,0.10))', color: 'var(--status-danger)' }}
    >
      <AlertTriangle size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
      <span className="font-medium">{message}</span>
      <span className="ms-auto flex items-center gap-2">
        {isAdmin ? (
          <Link
            to="/billing"
            data-testid="banner-pay-cta"
            className="h-7 px-3 inline-flex items-center rounded-brand-md text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--status-danger)' }}
          >
            {t('billing.banner_pay_cta')}
          </Link>
        ) : (
          <span className="text-xs text-secondary">{t('billing.banner_info')}</span>
        )}
        <button
          onClick={() => setDismissed(true)}
          aria-label={t('billing.banner_dismiss')}
          className="p-1 rounded-brand-md hover:bg-surface-hover"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}
